import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import appQuizApi from '../../../../api/appQuiz';
import quizRewardApi from '../../../../api/quizReward';
import QuizCard from './QuizCard';
import CreateQuizModal from './CreateQuizModal';
import EditQuizModal from './EditQuizModal';
import ImportQuizModal from './ImportQuizModal';
import QuizFilters from './QuizFilters';
import QuestionViewModal from './QuestionViewModal';

const QuizTemplateManagementContain = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [viewingQuiz, setViewingQuiz] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    thumbnail: null,
    isActive: true,
    displayQuestions: 3,
    questions: [],
    questionMedias: [],
    createRewards: false,
    rewardsData: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load quizzes
  useEffect(() => {
    loadQuizzes();
  }, [statusFilter]);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await appQuizApi.getAllQuizTemplates(params);
      
      // Handle API response structure
      const quizzesData = response.data?.quizzes || response.quizzes || response.data?.quizTemplates || response.quizTemplates || response.data || response;
      setQuizzes(Array.isArray(quizzesData) ? quizzesData : []);
    } catch (err) {
      console.error('❌ Error loading quiz templates:', err);
      setError('Không thể tải danh sách quiz templates');
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleCreateModal = () => {
    setCreateModal(!createModal);
    if (!createModal) {
      resetForm();
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setEditingQuiz(null);
      resetForm();
    }
  };

  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (!viewModal) {
      setViewingQuiz(null);
    }
  };

  const toggleImportModal = () => {
    setImportModal(!importModal);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      thumbnail: null,
      isActive: true,
      displayQuestions: 3,
      questions: [],
      questionMedias: [],
      createRewards: true,
      rewardsData: []
    });
    setIsSubmitting(false);
  };

  const handleEdit = async (quiz) => {
    try {
      
      // Load full quiz details with questions
      const response = await appQuizApi.getQuizTemplateById(quiz.id);
      
      const fullQuiz = response.data?.quiz || response.data?.quizTemplate || response.quizTemplate || response.data || response;
      
      setEditingQuiz(fullQuiz);
      setFormData({
        name: fullQuiz.name,
        thumbnail: null, // Don't load thumbnail for editing
        thumbnailUrl: fullQuiz.url || fullQuiz.thumbnailUrl, // Load thumbnail URL for preview
        isActive: fullQuiz.isActive,
        displayQuestions: fullQuiz.displayQuestions || 3,
        questions: fullQuiz.questions || [],
        questionMedias: []
      });
      setEditModal(true);
    } catch (err) {
      console.error('❌ Error loading quiz for edit:', err);
      setError('Không thể tải chi tiết quiz để chỉnh sửa');
    }
  };

  const handleViewQuestions = async (quiz) => {
    try {
      // Load full quiz details with questions
      const response = await appQuizApi.getQuizTemplateById(quiz.id);
      
      // Handle different API response structures
      const fullQuiz = response.data?.quiz || response.data?.quizTemplate || response.quizTemplate || response.data || response;
      
      setViewingQuiz(fullQuiz);
      setViewModal(true);
    } catch (err) {
      console.error('❌ Error loading quiz details:', err);
      setError('Không thể tải chi tiết quiz');
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      // Create new quiz
      if (!formData.thumbnail) {
        setError('Vui lòng chọn thumbnail');
        setIsSubmitting(false);
        return;
      }
      if (formData.questions.length === 0) {
        setError('Vui lòng thêm ít nhất 1 câu hỏi');
        setIsSubmitting(false);
        return;
      }
      
      // ⚠️ WARNING: Kiểm tra rewards trước khi tạo quiz
      if (!formData.createRewards || !formData.rewardsData || formData.rewardsData.length === 0) {
        const confirmed = window.confirm(
          '⚠️ CẢNH BÁO QUAN TRỌNG!\n\n' +
          'Quiz không có phần thưởng sẽ KHÔNG hoạt động trên mobile client.\n\n' +
          '❌ Client sẽ bị lỗi khi user hoàn thành quiz\n' +
          '❌ User không nhận được phần thưởng\n' +
          '❌ Quiz sẽ không hiển thị kết quả\n\n' +
          'Bạn có CHẮC CHẮN muốn tạo quiz không có rewards?\n\n' +
          '👉 Khuyến nghị: Nhấn Cancel và bật "Tạo phần thưởng tự động"'
        );
        
        if (!confirmed) {
          setIsSubmitting(false);
          return; // STOP - Không submit, user quay lại modal
        }
        
      }
      
      // Normalize questions - đảm bảo mỗi question có timeLimit hợp lệ (mặc định 30 nếu chưa có)
      const normalizedQuestions = formData.questions.map(q => ({
        ...q,
        timeLimit: (q.timeLimit && q.timeLimit >= 1 && q.timeLimit <= 300) ? q.timeLimit : 30
      }));

      const normalizedFormData = {
        ...formData,
        questions: normalizedQuestions
      };
      
      // Step 1: Create quiz
      const quizResponse = await appQuizApi.createQuizTemplate(normalizedFormData);
      const newQuizId = quizResponse.data?.quiz?.id || quizResponse.quiz?.id || quizResponse.data?.id;
      
      if (!newQuizId) {
        throw new Error('Không nhận được Quiz ID từ server');
      }
      
      
      // Step 2: Create rewards if enabled
      if (formData.createRewards && formData.rewardsData && formData.rewardsData.length > 0) {
        
        try {
          await quizRewardApi.createBulkRewards(newQuizId, formData.rewardsData);
          setSuccess('✅ Tạo quiz và rewards thành công!');
        } catch (rewardErr) {
          console.error('❌ Reward creation failed:', rewardErr);
          setError(
            `⚠️ Quiz đã tạo (ID: ${newQuizId}), nhưng không thể tạo rewards!\n\n` +
            `Client sẽ KHÔNG hoạt động. Vui lòng vào trang Rewards để thêm ngay!`
          );
          setTimeout(() => setError(null), 8000);
          toggleCreateModal();
          loadQuizzes();
          return;
        }
      } else {
        // Không có rewards - Warning
        setSuccess(
          `⚠️ Quiz đã tạo (ID: ${newQuizId}) - CHƯA CÓ REWARDS!\n\n` +
          `Client sẽ KHÔNG hoạt động. Vui lòng vào trang Rewards để thêm ngay!`
        );
        setTimeout(() => setSuccess(null), 8000);
      }
      
      toggleCreateModal();
      loadQuizzes();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error creating quiz template:', err);
      setError(err.response?.data?.message || 'Không thể tạo quiz template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      // Normalize questions - đảm bảo mỗi question có timeLimit hợp lệ (mặc định 30 nếu chưa có)
      const normalizedQuestions = formData.questions.map(q => ({
        ...q, // Giữ nguyên tất cả fields bao gồm id
        timeLimit: (q.timeLimit && q.timeLimit >= 1 && q.timeLimit <= 300) ? q.timeLimit : 30
      }));

      const normalizedFormData = {
        ...formData,
        questions: normalizedQuestions
      };
      
      // Update existing quiz
      await appQuizApi.updateQuizTemplate(editingQuiz.id, normalizedFormData);
      setSuccess('Cập nhật quiz template thành công!');
      
      toggleEditModal();
      loadQuizzes();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error updating quiz template:', err);
      setError(err.response?.data?.message || 'Không thể cập nhật quiz template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa quiz template này? (Sẽ xóa tất cả câu hỏi liên quan)')) return;
    
    try {
      await appQuizApi.deleteQuizTemplate(id);
      setSuccess('Xóa quiz template thành công!');
      loadQuizzes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting quiz template:', err);
      setError('Không thể xóa quiz template');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await appQuizApi.toggleQuizTemplateActive(id);
      setSuccess('Cập nhật trạng thái thành công!');
      loadQuizzes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  const handleImportSubmit = async (importData) => {
    try {
      setError(null);
      setIsImporting(true);
      
      // Call import API
      const response = await appQuizApi.importQuizFromExcel(importData);
      
      setSuccess('✅ Import quiz từ Excel thành công!');
      toggleImportModal();
      loadQuizzes();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error importing quiz from Excel:', err);
      setError(err.response?.data?.message || 'Không thể import quiz từ Excel. Vui lòng kiểm tra lại file Excel và thử lại.');
    } finally {
      setIsImporting(false);
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Quiz' title='Quiz Templates' mainTitle='Quiz Templates' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải quiz templates...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Quiz' title='Quiz Templates' mainTitle='Quản lý Quiz Templates' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-question-circle me-2 text-primary"></i>
                  Quản lý Quiz Templates
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="success" onClick={toggleImportModal} className="me-2">
                  <i className="fa fa-file-excel me-2"></i>
                  Import từ Excel
                </Button>
                <Button color="primary" onClick={toggleCreateModal}>
                  <i className="fa fa-plus me-2"></i>
                  Tạo Quiz Template Mới
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <QuizFilters 
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Quiz Templates List */}
        <Row>
          {quizzes.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-question-circle text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có quiz template nào</p>
                  <Button color="primary" onClick={toggleCreateModal}>
                    <i className="fa fa-plus me-2"></i>
                    Tạo Quiz Template Mới
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ) : (
            quizzes.map((quiz) => (
              <Col lg="4" md="6" key={quiz.id}>
                <QuizCard 
                  quiz={quiz}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onViewQuestions={handleViewQuestions}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create Modal */}
        <CreateQuizModal 
          isOpen={createModal}
          toggle={toggleCreateModal}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateSubmit}
          isSubmitting={isSubmitting}
        />

        {/* Edit Modal */}
        <EditQuizModal 
          isOpen={editModal}
          toggle={toggleEditModal}
          editingQuiz={editingQuiz}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleEditSubmit}
        />

        {/* View Questions Modal */}
        <QuestionViewModal 
          isOpen={viewModal}
          toggle={toggleViewModal}
          quiz={viewingQuiz}
        />

        {/* Import Quiz Modal */}
        <ImportQuizModal 
          isOpen={importModal}
          toggle={toggleImportModal}
          onSubmit={handleImportSubmit}
          isSubmitting={isImporting}
        />
      </Container>
    </Fragment>
  );
};

export default QuizTemplateManagementContain;

